/**
 * "Needs your attention" — ONE derivation, rendered twice (117 / 115-A3b).
 *
 * ⭐ The card on Overview and the list under the Attention tab are the SAME feed: the card
 * is this list, capped, with a footer that names the total. They are not two queries that
 * agree by luck, and they are not two filters over the same rows written in two components
 * — either arrangement drifts the moment one of them gains a rule.
 *
 * 🔴 `total` IS NOT `items.length`, and that distinction is the 007 defect. A capped list
 * under an uncapped count reads as complete: the page showed 50 overdue invoices, the KPI
 * said 60, and nobody could tell which number was the shop. Here the cap and the total are
 * carried together, so a renderer cannot show one without being able to show the other.
 */

export type AttentionKind = 'unassigned' | 'overdue';

export type AttentionItem = {
  /** Stable across renders — the underlying row's id, not an index. */
  key: string;
  kind: AttentionKind;
  /** The name a person recognises: a customer, not an id. */
  who: string;
  /** What is wrong with it, in the reader's words. */
  what: string;
  /** Where the fix lives. */
  to: string;
  action: string;
};

export type AttentionFeed = {
  /** The rows this render actually has. */
  items: AttentionItem[];
  /** How many there are in the SHOP — past any cap. Never `items.length`. */
  total: number;
  /**
   * True when the server holds more than `items` shows. Separate from `total > items.length`
   * so a renderer cannot accidentally compute it from a capped set and get `false`.
   */
  truncated: boolean;
};

export type UnassignedVisit = {
  visitId: string;
  customerName: string;
  /** Already formatted in the shop's zone by the caller — one clock, resolved once. */
  time: string;
};

export type OverdueInvoice = {
  invoiceId: string;
  customerName: string | null;
  balance: string;
  daysLate: number | null;
};

/**
 * Build the feed.
 *
 * `overdueTotal` is the SERVER's count of overdue invoices (`invoice.list({overdue}).total`),
 * not the length of `overdue`. `unassigned` needs no such argument: `visit.board` returns a
 * whole day unpaged, so its length IS the shop's count — an asymmetry worth stating, because
 * the obvious "make them consistent" refactor would silently re-introduce a capped total.
 */
export function attentionFeed(
  unassigned: readonly UnassignedVisit[],
  overdue: readonly OverdueInvoice[],
  overdueTotal: number,
): AttentionFeed {
  const items: AttentionItem[] = [
    ...unassigned.map((v) => ({
      key: `visit:${v.visitId}`,
      kind: 'unassigned' as const,
      who: v.customerName,
      what: `at ${v.time} has nobody assigned`,
      to: '/schedule',
      action: 'Assign',
    })),
    ...overdue.map((i) => ({
      key: `invoice:${i.invoiceId}`,
      kind: 'overdue' as const,
      // The data-honesty dash, and only where the name is genuinely absent.
      who: i.customerName ?? '—',
      what:
        i.daysLate === null
          ? `owes ${i.balance}`
          : `owes ${i.balance} — ${i.daysLate} ${i.daysLate === 1 ? 'day' : 'days'} late`,
      to: '/money',
      action: 'Chase',
    })),
  ];
  const total = unassigned.length + overdueTotal;
  return { items, total, truncated: total > items.length };
}

/**
 * The Overview card's slice, with the footer's number.
 *
 * ⚠️ The footer says the TOTAL, never `Math.min(cap, …)` and never the slice's length. A
 * "View all (5)" under five rows tells a reader they are looking at everything, which is
 * the exact lie the 007 defect told.
 */
export function attentionPreview(
  feed: AttentionFeed,
  cap: number,
): { items: AttentionItem[]; hidden: number; total: number } {
  const items = feed.items.slice(0, cap);
  return { items, hidden: Math.max(0, feed.total - items.length), total: feed.total };
}
